import type { ButtonHTMLAttributes, ReactNode } from "react";
import { useCallback, useState } from "react";
import styles from "./Button.module.css";

type Variant = "primary" | "secondary" | "ghost" | "destructive";
type Size = "md" | "sm";

interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick"> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  /** When set, button is disabled AND the reason renders as helper text. */
  disabledReason?: string;
  fullWidth?: boolean;
  icon?: ReactNode;
  children: ReactNode;
  /**
   * The click handler. If it returns a Promise, the button auto-enters
   * the loading state until the promise settles. This is the "disables
   * immediately on click, before the network returns" rule from §9.
   *
   * Return type is `unknown` (accepts anything) — callers often discard
   * the result of another helper (e.g. `toast.push(...)` returns an id).
   * The runtime code only treats the value specially if it's a thenable.
   */
  onClick?: (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => unknown | Promise<unknown>;
}

/**
 * Button — one component, four variants, one loading rule.
 *
 * Design decisions worth naming:
 *
 * 1. `destructive` is a GHOST outline with ember text, NOT a filled red
 *    button. Filled red on a payments app makes every close-account or
 *    remove-source action feel like nuclear launch. Ghost + ember says
 *    "you can do this; be sure."
 *
 * 2. Loading state is entered SYNCHRONOUSLY on click before onClick's
 *    promise settles. A user tap must never fire twice while the first
 *    request is still on the wire.
 *
 * 3. `disabledReason` is required in practice — a bare disabled button
 *    with no explanation is one of the top usability failures in
 *    fintech UI. This component enforces the pattern by giving reason
 *    a dedicated slot; use it.
 */
export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  disabled = false,
  disabledReason,
  fullWidth = false,
  icon,
  children,
  onClick,
  type = "button",
  className,
  ...rest
}: ButtonProps) {
  const [autoLoading, setAutoLoading] = useState(false);
  const isLoading = loading || autoLoading;
  const isDisabled = disabled || !!disabledReason || isLoading;

  const handleClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isDisabled || !onClick) return;
      const result = onClick(e);
      if (result && typeof (result as Promise<unknown>).then === "function") {
        setAutoLoading(true);
        try {
          await result;
        } finally {
          setAutoLoading(false);
        }
      }
    },
    [isDisabled, onClick],
  );

  const classes = [
    styles.button,
    styles[variant],
    styles[size],
    isLoading ? styles.loading : null,
    fullWidth ? styles.fullWidth : null,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span
      style={{ display: fullWidth ? "block" : "inline-block", width: fullWidth ? "100%" : "auto" }}
    >
      <button
        {...rest}
        type={type}
        disabled={isDisabled}
        onClick={handleClick}
        className={classes}
        aria-busy={isLoading || undefined}
      >
        {isLoading ? (
          <span aria-hidden className={styles.spinner} />
        ) : icon ? (
          <span aria-hidden>{icon}</span>
        ) : null}
        {children}
      </button>
      {disabledReason ? (
        <span className={styles.reason}>{disabledReason}</span>
      ) : null}
    </span>
  );
}
